import {Crop} from 'react-image-crop';

export const MAX_FILE_SIZE = 21 * 1024 * 1024;

export const getBase64SizeInKb = async (photo: string) => {
  try {
    const file = await dataUrlToFile(photo, 'pic');
    return file.size;
  } catch (e) {
    return 99999999;
  }
};
export const getBase64Extension = (photo: string) => {
  return photo.substring('data:image/'.length, photo.indexOf(';base64'));
};

export async function createFileFromHTMLImageElementAndCrop(
  image: HTMLImageElement,
  crop: Crop,
  orgImg: File
): Promise<{
  file: File | null;
  preview: string | unknown;
} | null> {
  try {
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const croppedWidth = crop.width * scaleX;
    const croppedHeight = crop.height * scaleY;

    const maxDim = 1000;
    let outputWidth = croppedWidth;
    let outputHeight = croppedHeight;

    const widthRatio = maxDim / croppedWidth;
    const heightRatio = maxDim / croppedHeight;
    const scaleDownRatio = Math.min(widthRatio, heightRatio, 1); // never upscale

    outputWidth = croppedWidth * scaleDownRatio;
    outputHeight = croppedHeight * scaleDownRatio;

    const canvas = new OffscreenCanvas(outputWidth, outputHeight);
    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        croppedWidth,
        croppedHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );
    }

    const blob = await canvas.convertToBlob({type: 'image/jpeg', quality: 0.5});
    const file = new File([blob], orgImg?.name ?? 'cropped.jpg', {
      type: 'image/jpeg',
    });
    const base64 = await toBase64(file);

    return {file, preview: base64};
  } catch (e) {
    console.error('ImageService::createFileFromHTMLImageElementAndCrop:', e);
    return null;
  }
}

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export async function dataUrlToFile(
  dataUrl: string,
  fileName: string
): Promise<File> {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  // Keep the real MIME type - labelling a JPEG as PNG makes the backend re-encode it badly.
  const type = blob.type || 'image/png';
  const extension = type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'png';
  const name = fileName.includes('.') ? fileName : `${fileName}.${extension}`;
  return new File([blob], name, {type});
}

export function getFileExtension(filename: string) {
  return filename.split('.').pop();
}
